import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { canSee, captureSummaryV3, financeFacility, performanceOutcomes, type BenchMetricKey, type OutcomeMeasure } from '@aithora/shared';
import { useConsoleRole } from '../../session/SessionContext';
import { sx } from '../../ui/designStyles';
import { CareOpsRisk } from './CareOpsRisk';
import { usePerf } from './ctx';
import { GRADE_STYLE, gradeOf, marginPct, notServed, ord, pc, pct100, pre, usePerfApi } from './lib';
import { BuildTagged, useCustomerText, useBuildLabels } from '../../ui/buildLabels';

type Dom = 'Quality' | 'Staffing' | 'Inspection' | 'Money';
interface BoardRow {
  dom: Dom;
  label: string;
  value: string;
  pctl: number | null;
  src: string;
}
const FILTS = ['All', 'Quality', 'Staffing', 'Inspection', 'Money'] as const;
type Filt = (typeof FILTS)[number];

/** A CMS metric's value as the design prints it (design scVal formats). */
function fmtMetric(key: BenchMetricKey, v: number | null | undefined): string {
  if (v == null) return '–';
  switch (key) {
    case 'qmRating':
    case 'overallRating':
    case 'staffingRating':
    case 'healthInspectionRating':
    case 'totalHprd':
    case 'rnHprd':
      return v.toFixed(2);
    case 'nurseTurnover':
    case 'rnTurnover':
      return v.toFixed(1) + '%';
    case 'healthSurveyScore':
      return v.toFixed(1);
    case 'numPenalties':
    case 'severeDeficiencies':
    case 'infectionDeficiencies':
      return String(Math.round(v));
    case 'finesDollars':
      return '$' + Math.round(v).toLocaleString('en-US');
    case 'occupancy':
      return Math.round(v <= 1 ? v * 100 : v) + '%';
    case 'revenuePerBed':
      return '$' + Math.round(v / 1000) + 'K';
    case 'operatingMargin':
      return (marginPct(v) as number).toFixed(1) + '%';
    default:
      return String(v);
  }
}
const fmtOutcome = (m: OutcomeMeasure | undefined, perDay: string) =>
  m?.value == null ? '–' : m.unit === 'pct' ? m.value.toFixed(1) + '%' : m.value.toFixed(1) + perDay;
const srcOf = (m: { basis: string } | undefined) => (!m ? '–' : m.basis === 'measured' ? 'LIVE' : m.basis === 'modeled' ? 'MODELED' : 'SAMPLE');

const pill = (c: string, bg: string) => 'display:inline-flex;align-items:center;height:18px;padding:0 7px;border-radius:5px;font-size:14px;font-weight:700;white-space:nowrap;color:' + c + ';background:' + bg + ';';
/** The least height of the metric board and the grade panel side by side (wide layout). */
const SPLIT_MIN_PX = 520;
const kLbl = 'font-size:14px;letter-spacing:.12em;text-transform:uppercase;color:#54606C;font-weight:700;line-height:1.2;white-space:normal;overflow-wrap:normal;word-break:normal;hyphens:manual;text-wrap:balance;min-width:0;padding-right:6px;';
/** Each card is at least as wide as its longest word ("HOSPITALIZATIONS"), so a label wraps between words and never inside one. */
export const CARD_COLS = 'repeat(4,minmax(min-content,1fr))';

/** What an off-track card opens (design sbCards next), by the card's own label. */
export const CARD_NEXT: Record<string, string> = {
  Leaking: 'Open the held claims →',
  'Residents hurt in a fall': 'See who is at fall risk →',
  Falls: 'See who is at fall risk →',
  '30-day readmission': 'See recent discharges →',
  Hospitalizations: 'See who went out →',
  'Mood improved': 'See who is declining →',
  'Billable this month': 'Open Capture →',
};
const cardWrap = (i: number) =>
  'box-sizing:border-box;padding:9px 12px 11px;min-width:0;display:flex;flex-direction:column;' + (i ? 'border-left:1px solid rgba(34,39,46,.11);' : '') + 'animation:riseIn .3s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 55 + 'ms;';
const statusOf = (p: number | null): [string, string] =>
  p === null ? ['Not reported', '#54606C'] : p >= 75 ? ['Strong', '#1F6B52'] : p >= 50 ? ['Above median', '#2F5C97'] : p >= 25 ? ['Below median', '#5B6B7C'] : ['Weak', '#B3261E'];
const basisTag = (b: string) =>
  'display:inline-flex;align-items:center;height:16px;padding:0 6px;border-radius:3px;font-size:14px;font-weight:700;letter-spacing:.04em;white-space:nowrap;' +
  (b === 'MEASURED' ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : b === 'MODELED' ? 'color:#5B6B7C;background:rgba(91,107,124,.13);' : b === 'WHAT-IF' ? 'color:#2F5C97;background:rgba(79,138,214,.13);' : 'color:#54606C;background:rgba(34,39,46,.07);');

/**
 * Summary (design isOverview, T15969-16128): what Aithora delivered, the sixteen-metric
 * board against the peer set, the grade block, the selected metric's gap and drivers,
 * provenance, and the whole-facility margin ladder (a local what-if; nothing is written).
 */
export function SummaryTab() {
  const build = useBuildLabels();
  const plain = useCustomerText();
  const ctx = usePerf();
  const { fac, peerLabel, op, tier, caseMix } = ctx;
  const facility = op ? fac?.orgId ?? undefined : undefined;
  const enabled = op ? !!fac?.orgId : true;
  const out = usePerfApi((c, signal) => performanceOutcomes(c, { query: { facility, tier, caseMix: caseMix || undefined }, signal }), [facility, tier, caseMix], enabled);
  const cap = usePerfApi((c, signal) => captureSummaryV3(c, { query: { facility }, signal }), [facility], enabled);
  const fin = usePerfApi((c, signal) => financeFacility(c, { query: { facility }, signal }), [facility], enabled);
  const [filt, setFilt] = useState<Filt>('All');
  const [sortK, setSortK] = useState<'gap' | 'name'>('gap');
  const [selKey, setSelKey] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  // V9: the worst five cards, off-track first; Show all opens the rest.
  const [allCards, setAllCards] = useState(false);
  const navigate = useNavigate();
  const { role } = useConsoleRole();
  const [typed, setTyped] = useState<string | null>(null);
  const [wide, setWide] = useState(() => (typeof window === 'undefined' ? true : window.innerWidth >= 980));
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= 980);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);

  if (!fac) {
    return (
      <div data-screen-label="Overview" data-testid="perf-summary" aria-busy={ctx.facState.loading ? 'true' : undefined} style={sx('flex:1 1 auto;min-height:0;grid-column:1/-1;display:flex;flex-direction:column;gap:11px;min-width:0;padding:14px 0;font-size:16px;color:#54606C;')}>
        {ctx.facState.loading ? 'Loading the benchmark…' : ctx.facState.error ?? 'No building is graded yet.'}
      </div>
    );
  }

  const o = out.data;
  const M = o?.measures;
  // Percentiles in 0..100 (the wire carries 0..1, see pct100); values untouched.
  const met = (k: BenchMetricKey) => {
    const m = fac.metrics[k];
    return m ? { ...m, percentile: pct100(m.percentile) } : undefined;
  };
  const residents = fac.residents;
  // ── capture: booked (measured) and leaking (opportunity), never summed into one figure ──
  const bookedC = cap.data?.measured.value ?? null;
  const leakC = cap.data?.opportunity.value ?? null;
  const booked = bookedC == null ? null : bookedC / 100;
  const leak = leakC == null ? null : leakC / 100;
  const eligible = booked != null && leak != null ? booked + leak : null;
  const capRate = booked != null && eligible ? (booked / eligible) * 100 : null;
  // occupancy: the live census against the licensed beds when both are known
  const liveOcc = residents != null && fac.beds ? (residents / fac.beds) * 100 : null;

  const cms = (dom: Dom, label: string, key: BenchMetricKey): BoardRow => ({ dom, label, value: fmtMetric(key, met(key)?.value), pctl: met(key)?.percentile ?? null, src: met(key) ? 'CMS' : '–' });
  const live = (dom: Dom, label: string, m: OutcomeMeasure | undefined, value: string): BoardRow => ({ dom, label, value, pctl: m?.percentile ?? null, src: srcOf(m) });
  const flat: BoardRow[] = [
    cms('Quality', 'Quality measures', 'qmRating'),
    live('Quality', 'Unplanned hospitalization', M?.hospitalization, fmtOutcome(M?.hospitalization, ' /1k RD')),
    live('Quality', 'Injurious falls', M?.injuriousFalls, fmtOutcome(M?.injuriousFalls, '')),
    live('Quality', '30-day readmission', M?.readmission, fmtOutcome(M?.readmission, '')),
    cms('Staffing', 'Total HPRD', 'totalHprd'),
    cms('Staffing', 'RN HPRD', 'rnHprd'),
    cms('Staffing', 'Nurse turnover', 'nurseTurnover'),
    cms('Staffing', 'RN turnover', 'rnTurnover'),
    cms('Inspection', 'Survey score', 'healthSurveyScore'),
    cms('Inspection', 'Severe deficiencies', 'severeDeficiencies'),
    cms('Inspection', 'Penalties', 'numPenalties'),
    cms('Inspection', 'Fines', 'finesDollars'),
    liveOcc != null
      ? { dom: 'Money', label: 'Occupancy', value: Math.round(liveOcc) + '%', pctl: met('occupancy')?.percentile ?? null, src: 'LIVE' }
      : cms('Money', 'Occupancy', 'occupancy'),
    cms('Money', 'Revenue / bed', 'revenuePerBed'),
    live('Money', 'Reimbursement capture', M?.capture, capRate == null ? '–' : capRate.toFixed(1) + '%'),
    cms('Money', 'Operating margin', 'operatingMargin'),
  ];
  const scored = flat.filter((r) => r.pctl !== null);
  const worst = scored.slice().sort((a, b) => (a.pctl as number) - (b.pctl as number))[0] ?? null;
  const belowN = scored.filter((r) => (r.pctl as number) < 50).length;
  const rows = flat
    .filter((r) => filt === 'All' || r.dom === filt)
    .slice()
    .sort((a, b) => (sortK === 'name' ? a.label.localeCompare(b.label) : (a.pctl ?? 999) - (b.pctl ?? 999)));
  const key = (r: BoardRow) => r.dom + '|' + r.label;
  const sel = flat.find((r) => key(r) === (selKey ?? (worst ? key(worst) : ''))) ?? worst;

  // ── the grade block ──
  const comp = fac.composite;
  const grade = fac.grade ?? (comp != null ? gradeOf(comp) : null);
  const gs = GRADE_STYLE[grade ?? 'C'] ?? GRADE_STYLE.C;
  const peerSmall = (ctx.peer?.n ?? fac.peerN ?? 0) < 11;
  const overall = met('overallRating')?.percentile ?? null;
  const bandP = peerSmall ? null : overall;
  const bandPos = bandP === null ? 50 : Math.max(0, Math.min(100, ((bandP - 10) / 80) * 100));
  const bandNote =
    bandP === null ? 'not scored against the peer band' : bandP > 90 ? 'above the p90 end of the band · ' + ord(bandP) : bandP < 10 ? 'below the p10 end of the band · ' + ord(bandP) : 'positioned against the ' + (ctx.peer?.name ?? 'national') + ' peer band';
  const fc = !peerSmall && o?.forecast ? o.forecast : null;
  const fcPos = fc ? Math.max(0, Math.min(100, ((fc.percentile - 10) / 80) * 100)) : 0;
  const fcLine = fc ? '30-day: ' + ord(fc.percentile) + (fc.direction === 'up' ? ' ▲' : ' ▼') : '30-day projection needs a scored peer band';
  const est = (b?: string) => (!build ? '' : b === 'modeled' ? ' · EST.' : b === 'measured' ? '' : ' · SAMPLE');
  const whyPool = (
    [
      ['Staffing', met('staffingRating')?.percentile],
      ['Quality measures', met('qmRating')?.percentile],
      ['Health inspection', met('healthInspectionRating')?.percentile],
      ['Occupancy', met('occupancy')?.percentile],
      ['Operating margin', met('operatingMargin')?.percentile],
      ['Nurse turnover', met('nurseTurnover')?.percentile],
    ] as [string, number | null | undefined][]
  )
    .filter((w): w is [string, number] => w[1] != null)
    .sort((a, b) => b[1] - a[1]);
  const why = whyPool.length >= 3 ? [whyPool[0], whyPool[1], whyPool[whyPool.length - 1]] : whyPool;

  // ── the selected metric ──
  const selHas = !!sel && sel.pctl !== null;
  const sp = sel?.pctl ?? 0;
  const selVerdict = !sel
    ? ''
    : !selHas
      ? 'NOT REPORTED · below the CMS reporting floor'
      : (sp < 50 ? 'BELOW TYPICAL' : 'AT OR ABOVE TYPICAL') + ' · ' + ord(sp) + ' percentile · ' + Math.abs(sp - 50) + ' points ' + (sp < 50 ? 'below' : 'above') + ' what is typical for peers';
  const selGap = !sel
    ? ''
    : !selHas
      ? 'Too few cases to report against ' + peerLabel + '. No percentile, so no gap: the value stands on its own.'
      : sp < 50
        ? 'Closing this gap moves ' + sel.label.toLowerCase() + ' from the ' + ord(sp) + ' to the 50th percentile against ' + peerLabel + '.'
        : sel.label + ' already sits ' + (sp - 50) + ' points above what is typical for peers. Holding it there is the work.';
  const selBasis = !sel
    ? ''
    : !selHas
      ? 'Source ' + sel.src + '. The reporting floor is a CMS rule, not a score.'
      : 'Source ' + sel.src + ' · percentile against ' + peerLabel + '. CMS publishes the percentile, not the peer distribution, so the value at the median is not in this build.';
  const selVerdictStyle = 'font-size:16px;font-weight:700;line-height:1.4;text-wrap:pretty;color:' + (!selHas ? '#54606C' : sp < 50 ? '#8F1F18' : '#12503C') + ';';

  // ── the margin ladder (whole facility, a what-if on the page only) ──
  const measuredMargin = marginPct(met('operatingMargin')?.value);
  const typedOn = typed !== null && typed !== '';
  const mlBase = typedOn ? Math.max(-20, Math.min(40, Number(typed) || 0)) : measuredMargin !== null ? measuredMargin : 2.0;
  const mlBasis = typedOn ? 'WHAT-IF' : measuredMargin !== null ? 'MEASURED' : 'BENCHMARK';
  const mlBasisNote =
    mlBasis === 'WHAT-IF' ? 'your figure · nothing is overwritten' : mlBasis === 'MEASURED' ? 'CMS cost report · the same figure the Operating margin row reports' : 'all-payer operating margin, MedPAC 2024 · no margin on the CMS record for ' + fac.name;
  const lad = o?.ladder ?? null;
  const revMo = lad ? lad.revenueMonthlyCents / 100 : null;
  const mlPts = (d: number) => (revMo ? (d / revMo) * 100 : 0);
  const ML =
    lad && booked != null && eligible != null
      ? [
          { k: 'Today', usd: 0, basis: mlBasis },
          { k: 'At current capture', usd: booked, basis: cap.data?.measured.basis === 'measured' ? 'MEASURED' : 'MODELED' },
          { k: 'At full enrollment', usd: eligible, basis: 'MODELED' },
          { k: 'With RPM enabled', usd: eligible + lad.rpmHeadroomCents / 100, basis: 'MODELED' },
          { k: 'Top of the published band', usd: lad.bandTopCents / 100, basis: 'MODELED' },
        ]
      : null;
  const mlPerRes = eligible != null && residents ? eligible / residents : null;

  // ── the strip, the chips ──
  const inj = M?.injuriousFalls;
  const readm = M?.readmission;
  const readmRef = readm?.reference ?? null;
  const phq = o?.phq9;
  const phqOk = !!phq && phq.cohort >= phq.floor;
  const over90 = fin.data?.sections.receivables.over90Days.value ?? null;
  const cards = [
    // Capture's "Measured" for this building (GET /capture/summary): documented and billable
    // this month, not yet billed. Not Finance's "Collected" (paid claims), so not "captured".
    { k: 'Billable this month', v: booked == null ? '–' : '$' + Math.round(booked).toLocaleString('en-US'), foot: 'documented, not yet billed · of $' + (eligible == null ? '–' : Math.round(eligible).toLocaleString('en-US')) + ' eligible', c: '#12503C' },
    {
      k: 'Residents hurt in a fall',
      v: inj?.value == null ? '–' : inj.value.toFixed(1) + '%',
      foot: inj?.value == null ? 'not measured yet' : (inj.value < (inj.reference?.value ?? 3.4) ? 'below' : 'above') + ' the ' + (inj.reference?.value ?? 3.4) + '% US average',
      c: inj?.value == null ? '#54606C' : inj.value < (inj.reference?.value ?? 3.4) ? '#12503C' : '#B3261E',
    },
    { k: 'Hospitalizations', v: M?.hospitalization.value == null ? '–' : M.hospitalization.value.toFixed(1), foot: 'hospital stays per 1,000 resident days', c: '#22272E' },
    { k: 'Leaking', v: leak == null ? '–' : '$' + Math.round(leak).toLocaleString('en-US'), foot: 'eligible but not captured this period', c: leak == null ? '#54606C' : leak > 0 ? '#B3261E' : '#12503C' },
    {
      k: 'Falls',
      v: M?.falls.value == null ? '–' : M.falls.value.toFixed(1),
      foot: 'all falls per 1,000 resident days' + (M?.falls.reference ? ' · peer ' + M.falls.reference.value.toFixed(1) : ''),
      c: '#22272E',
    },
    {
      k: '30-day readmission',
      v: readm?.value == null ? '–' : readm.value.toFixed(1) + '%',
      // The reference is the server's (reference-benchmarks.ts: CMS Care Compare US average, with its date); no fallback figure.
      foot: readm?.value == null ? 'not measured yet' : readmRef == null ? 'no US reference on file' : (readm.value < readmRef.value ? 'below' : 'above') + ' the ' + readmRef.value + '% US average' + (readmRef.asOf ? ' (CMS, ' + readmRef.asOf.slice(0, 7) + ')' : ''),
      c: readm?.value == null || readmRef == null ? '#54606C' : readm.value < readmRef.value ? '#12503C' : '#B3261E',
    },
    {
      k: 'Mood improved',
      v: phqOk ? String(phq!.cohort) : '–',
      pill: phqOk ? null : 'building',
      foot: phqOk ? 'PHQ-9 responders this window' : phq ? 'building · insufficient n (' + phq.cohort + ' of ' + phq.floor + ')' : 'not measured yet',
      c: phqOk ? '#12503C' : '#54606C',
    },
  ];
  // V9 (design sbCards): off-track cards first (stable), the worst five unless Show all is
  // open, each opening its list, an off-track one with its next step in red; and one line
  // above them naming how many are off track and where to start.
  const RED = '#B3261E';
  const ranked = cards.map((c, i) => ({ c, i })).sort((a, b) => Number(b.c.c === RED) - Number(a.c.c === RED) || a.i - b.i).map((x) => x.c);
  const shownCards = allCards ? ranked : ranked.slice(0, 5);
  const off = ranked.filter((c) => c.c === RED);
  const cardPath = (k: string) => (k === 'Billable this month' || k === 'Leaking' ? '/capture' : '/residents');
  const canOpen = (k: string) => canSee(role, k === 'Billable this month' || k === 'Leaking' ? 'capture' : 'residents');
  const goCard = (k: string) => {
    if (canOpen(k)) navigate(cardPath(k));
  };
  const nextOf = (k: string) => CARD_NEXT[k] ?? 'Open the list →';
  const topLine = off.length === 0 ? 'Everything here is on track this month.' : off.length + ' of ' + cards.length + ' off track this month. Start with ' + off[0].k.toLowerCase() + '.';
  const chips: [string, string, boolean][] = [
    // Nothing left undocumented is not "fully captured" while documented care waits to be billed.
    [
      'Capture',
      leak == null ? plain('not served yet') : leak === 0 ? (booked ? 'fully documented · $' + Math.round(booked).toLocaleString('en-US') + ' not yet billed' : 'fully captured') : '$' + Math.round(leak).toLocaleString('en-US') + ' leaking',
      leak === 0 && !booked,
    ],
    ['Inj. falls', inj?.value == null ? 'not measured yet' : (inj.value < (inj.reference?.value ?? 3.4) ? 'below' : 'above') + ' the US average', inj?.value != null && inj.value < (inj.reference?.value ?? 3.4)],
    ['Below median', belowN === 0 ? 'none under the median' : belowN + ' of ' + scored.length + ' under the median', belowN === 0],
    ['PHQ-9', !phq ? 'not measured yet' : phqOk ? (phq.responders ?? 0) + ' of ' + phq.cohort + ' scored ≥10' : 'n=' + phq.cohort + ', below the floor of ' + phq.floor, phqOk],
    ['A/R', over90 == null ? plain('not served yet') : over90 > 0 ? '$' + (over90 / 1000).toFixed(0) + 'K aged >90d' : 'none aged in range', over90 === 0],
    ['Incidents', o?.openIncidents == null ? plain('not served yet') : o.openIncidents > 0 ? o.openIncidents + ' unresolved' : 'none open', o?.openIncidents === 0],
  ];
  const how = 'How: Atlas check-ins and care-team alerts catch changes early, and every eligible measure is captured so nothing is billed late or missed.';
  const panel = 'min-width:0;display:flex;flex-direction:column;border:1px solid rgba(34,39,46,.13);border-radius:11px;background:#FCFAF4;';
  // The board and the grade panel keep at least SPLIT_MIN_PX, enough for the grade, what moves
  // it, the selected metric's drivers and the provenance with no inner scroll at a laptop's
  // height; the page scrolls instead, and Care operations sits below them.
  const splitStyle = wide
    ? 'flex:1 0 ' + SPLIT_MIN_PX + 'px;min-height:' + SPLIT_MIN_PX + 'px;overflow:hidden;display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:10px 12px;align-items:stretch;min-width:0;'
    : 'flex:1 1 auto;min-height:0;overflow:hidden;display:flex;flex-direction:column;gap:10px;min-width:0;';
  const boardStyle = panel + 'min-height:0;overflow:hidden;' + (wide ? '' : detailOpen ? 'display:none;' : 'flex:1 1 auto;');
  // The right panel is ONE scroll box: every row takes its full height (the last row used to
  // be squeezed to fit, which cut the margin ladder off with an inner scroll that never had
  // room to move), and the panel's own scrollbar reaches the bottom of it.
  const detailStyle =
    panel +
    'min-height:0;overflow-y:auto;overflow-x:hidden;' +
    (wide ? 'display:grid;grid-template-columns:repeat(2,minmax(0,1fr));grid-auto-rows:auto;align-content:start;gap:0 10px;align-items:stretch;' : detailOpen ? 'flex:1 1 auto;' : 'display:none;');
  const outMissing = notServed(out.error);

  return (
    <div data-screen-label="Overview" data-testid="perf-summary" style={sx('flex:1 1 auto;min-height:0;grid-column:1/-1;display:flex;flex-direction:column;gap:11px;min-width:0;' + (wide ? 'overflow-y:auto;overflow-x:hidden;padding-right:3px;' : ''))}>
      <div style={sx('flex:0 0 auto;display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:2px;min-width:0;')}>
        <span style={sx('font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;white-space:nowrap;')}>What Aithora delivered</span>
        <span title={how} style={sx(wide ? 'flex:1 1 180px;min-width:0;font-size:14px;color:#54606C;line-height:1.3;text-wrap:pretty;' : 'display:none;')}>
          this period · measured from your own care records, not estimates
        </span>
        <span title={fc ? 'Straight line from the last 90 days of domain scores. An estimate, not a promise.' : ''} style={sx('display:inline-flex;align-items:baseline;padding:3px 9px;border-radius:7px;font-size:14px;font-weight:700;white-space:nowrap;color:#2F5C97;background:rgba(47,92,151,.09);')}>
          <span>{fcLine}</span>
          {fc ? est(fc.basis) : ''}
        </span>
        {o?.vbp && (
          <span
            title={'CMS payment program, ' + o.vbp.measures + ' measures. Direction from live signals; no payment estimate until baselines load.'}
            style={sx('display:inline-flex;align-items:baseline;gap:6px;padding:3px 9px;border-radius:7px;font-size:14px;white-space:nowrap;color:#2F5C97;background:rgba(47,92,151,.09);')}
          >
            <span style={sx('font-weight:700;letter-spacing:.06em;')}>CMS VBP</span>
            <span>{o.vbp.improving + ' ▲ · ' + o.vbp.worsening + ' ▼'}</span>
            {est(o.vbp.basis)}
          </span>
        )}
      </div>

      <div data-testid="perf-summary-top" style={sx('display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding:12px 14px;margin:8px 0;border-radius:10px;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;')}>
        <span style={sx('flex:1 1 300px;min-width:0;font-size:17px;font-weight:700;color:#22272E;line-height:1.4;')}>{topLine}</span>
        {off.length > 0 && canOpen(off[0].k) && (
          <a
            href="#"
            onClick={(e) => {
              pre(e);
              goCard(off[0].k);
            }}
            style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 16px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;')}
          >
            {nextOf(off[0].k)}
          </a>
        )}
      </div>

      <div
        data-testid="perf-summary-cards"
        aria-busy={(out.loading && !o) || (cap.loading && !cap.data) ? 'true' : undefined}
        style={sx('flex:0 0 auto;display:grid;gap:0;border:1px solid rgba(34,39,46,.13);border-radius:10px;background:#FCFAF4;min-width:0;' + (wide ? 'grid-template-columns:' + CARD_COLS + ';overflow:hidden;' : 'grid-template-columns:' + CARD_COLS + ';grid-template-rows:auto;grid-auto-rows:0;overflow:hidden;'))}
      >
        {shownCards.map((c, i) => (
          <div
            key={c.k}
            title={c.foot}
            data-testid="perf-card"
            role={canOpen(c.k) ? 'link' : undefined}
            tabIndex={canOpen(c.k) ? 0 : undefined}
            onClick={() => goCard(c.k)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') goCard(c.k);
            }}
            style={sx(cardWrap(i) + (canOpen(c.k) ? 'cursor:pointer;' : ''))}
          >
            <span style={sx(kLbl)}>{c.k}</span>
            <span style={sx('display:flex;align-items:baseline;gap:6px;flex-wrap:nowrap;overflow:hidden;margin-top:1px;min-width:0;')}>
              <span style={sx('font-weight:800;letter-spacing:-.025em;line-height:1.05;font-variant-numeric:tabular-nums;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;font-size:19px;color:' + c.c + ';')}>
                <span>{c.v}</span>
              </span>
              <span style={sx(c.pill ? pill('#54606C', 'rgba(34,39,46,.07)') : 'display:none;')}>{c.pill ?? ''}</span>
            </span>
            {/* The unit and comparison, on the card (2026-10-13): it was only a hover tooltip, so
                "Falls 7.9" (per 1,000 resident days) read like "7.9%" next to it. */}
            <span data-testid="perf-card-foot" style={sx('display:block;font-size:14px;color:#54606C;line-height:1.3;margin-top:2px;text-wrap:pretty;')}>{c.foot}</span>
            {c.c === RED && canOpen(c.k) && <span style={sx('font-size:14px;font-weight:700;color:#B3261E;overflow-wrap:break-word;margin-top:2px;')}>{nextOf(c.k)}</span>}
          </div>
        ))}
      </div>
      {ranked.length > 5 && (
        <div style={sx('display:flex;justify-content:flex-start;margin:6px 0;')}>
          <a
            href="#"
            data-testid="perf-cards-all"
            onClick={(e) => {
              pre(e);
              setAllCards((o) => !o);
            }}
            style={sx('display:inline-flex;align-items:center;min-height:36px;padding:0 14px;border-radius:8px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;color:#264C7E;border:1px solid rgba(47,92,151,.35);')}
          >
            {allCards ? 'Show the worst 5' : 'Show all ' + ranked.length}
          </a>
        </div>
      )}
      {outMissing && <div style={sx('flex:0 0 auto;font-size:14px;color:#54606C;')} data-testid="empty-state">{plain('Not served yet: the care outcomes Aithora measures (falls, hospitalizations, readmissions, mood) for this building.')}</div>}
      {out.error && !outMissing && (
        <div role="alert" style={sx('flex:0 0 auto;font-size:14px;color:#B3261E;')}>
          {out.error.message}
        </div>
      )}

      <div title={how} style={sx('flex:0 0 auto;display:flex;align-items:baseline;gap:6px 7px;flex-wrap:wrap;min-width:0;')}>
        {chips.map(([k, v, ok]) => (
          <span key={k} style={sx('display:inline-flex;align-items:baseline;gap:6px;padding:3px 9px;border-radius:7px;font-size:14px;white-space:nowrap;' + (ok ? 'color:#12503C;background:rgba(47,138,111,.11);' : 'color:#5B6B7C;background:rgba(91,107,124,.12);'))}>
            <span style={sx('font-weight:700;letter-spacing:.06em;text-transform:uppercase;')}>{k}</span>
            <span>{v}</span>
          </span>
        ))}
      </div>

      <div style={sx('flex:0 0 auto;display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;row-gap:3px;padding:6px 11px;border:1px solid rgba(34,39,46,.13);border-radius:9px;background:#FCFAF4;min-width:0;' + (wide ? 'display:none;' : ''))}>
        <span style={sx('font-size:16px;font-weight:700;letter-spacing:-.01em;min-width:0;text-wrap:pretty;')}>
          <span>{(grade ?? '–') + ' · ' + (comp ?? '–') + ' of 100 · ' + (overall != null ? ord(overall) : '–') + ' · ' + (why[2] ? why[2][0] + ' is the drag' : '')}</span>
        </span>
      </div>

      <div style={sx(splitStyle)}>
        <div style={sx(boardStyle)} data-testid="perf-board">
          <div style={sx('flex:0 0 auto;display:flex;align-items:stretch;gap:6px;flex-wrap:nowrap;overflow-x:auto;padding:6px 10px 7px;border-bottom:1px solid rgba(34,39,46,.11);min-width:0;')} role="group" aria-label="Domain">
            {FILTS.map((f) => {
              const mine = f === 'All' ? scored : scored.filter((r) => r.dom === f);
              const avg = mine.length ? Math.round(mine.reduce((t, r) => t + (r.pctl as number), 0) / mine.length) : null;
              const under = mine.filter((r) => (r.pctl as number) < 50).length;
              const tone = avg === null ? '#54606C' : avg >= 75 ? '#1F6B52' : avg >= 50 ? '#2F5C97' : avg >= 25 ? '#5B6B7C' : '#B3261E';
              const on = filt === f;
              return (
                <a
                  key={f}
                  href="#"
                  aria-pressed={on}
                  onClick={(e) => {
                    pre(e);
                    setFilt(f);
                  }}
                  style={sx('flex:1 1 0;min-width:0;display:block;padding:5px 9px 6px;border-radius:8px;text-decoration:none;color:inherit;border:1px solid ' + (on ? 'rgba(34,39,46,.30);background:rgba(34,39,46,.05);' : 'rgba(34,39,46,.12);'))}
                >
                  <span style={sx('display:block;font-size:14px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:' + (on ? '#22272E' : '#54606C') + ';')}>{f}</span>
                  <span style={sx('display:flex;align-items:baseline;gap:6px;flex-wrap:nowrap;min-width:0;')}>
                    <span style={sx('font-size:14px;font-weight:700;font-variant-numeric:tabular-nums;white-space:nowrap;color:' + tone + ';')}>{avg === null ? '–' : ord(avg)}</span>
                    <span style={sx('font-size:14px;color:' + (under ? '#B3261E' : '#54606C') + ';white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;')}>{mine.length ? under + ' of ' + mine.length + ' low' : 'not reported'}</span>
                  </span>
                  <span style={sx('display:block;height:3px;border-radius:2px;background:rgba(34,39,46,.10);margin-top:3px;overflow:hidden;')}>
                    <i style={sx('display:block;height:100%;border-radius:2px;width:' + (avg ?? 0) + '%;background:' + tone + ';transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) forwards;')} />
                  </span>
                </a>
              );
            })}
          </div>
          <div
            title={'Percentile is this facility against ' + peerLabel + ' on the same metric; vs median is that percentile minus 50. A metric below the CMS reporting floor is shown as-is and carries no gap: a facility with too few cases to report is not a facility performing at the median.'}
            style={sx('flex:0 0 auto;display:grid;grid-template-columns:minmax(0,1.5fr) 44px 64px 38px 66px 84px;gap:8px;padding:6px 10px;font-size:14px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#54606C;border-bottom:1px solid rgba(34,39,46,.14);min-width:0;')}
          >
            <span>Metric board</span>
            <BuildTagged tag="LIVE" keepSlot><span>Src</span></BuildTagged>
            <span style={sx('text-align:right;')}>Value</span>
            <span style={sx('text-align:right;')}>Pctl</span>
            <a
              href="#"
              onClick={(e) => {
                pre(e);
                setSortK((k) => (k === 'gap' ? 'name' : 'gap'));
              }}
              style={sx('text-align:right;text-decoration:none;color:#2F5C97;white-space:nowrap;')}
            >
              {sortK === 'gap' ? 'Gap ▼' : 'Name ▲'}
            </a>
            <span>Status</span>
          </div>
          <div style={sx(wide ? 'flex:1 1 auto;min-height:60px;overflow-y:auto;overflow-x:hidden;min-width:0;padding:2px 4px 4px;' : 'flex:1 1 auto;min-height:0;overflow-y:auto;overflow-x:hidden;min-width:0;padding:2px 4px 4px;')}>
            {rows.map((r, i) => {
              const stt = statusOf(r.pctl);
              const gap = r.pctl === null ? null : r.pctl - 50;
              const on = !!sel && key(r) === key(sel);
              return (
                <a
                  key={key(r)}
                  href="#"
                  aria-current={on ? 'true' : undefined}
                  data-testid="perf-board-row"
                  onClick={(e) => {
                    pre(e);
                    setSelKey(key(r));
                    setDetailOpen(true);
                  }}
                  style={sx(
                    'display:grid;grid-template-columns:minmax(0,1.5fr) 44px 64px 38px 66px 84px;gap:11px;align-items:center;padding:6px 10px;border-radius:7px;text-decoration:none;color:#22272E;border-bottom:1px solid rgba(34,39,46,.06);' +
                      (on ? 'background:rgba(79,138,214,.10);' : '') +
                      'animation:riseIn .24s cubic-bezier(.22,.61,.36,1);animation-delay:' + Math.min(280, i * 18) + 'ms;',
                  )}
                >
                  <span style={sx('min-width:0;')}>
                    <span style={sx('display:block;font-size:16px;font-weight:700;line-height:1.25;text-wrap:pretty;')}>{r.label}</span>
                    <span style={sx('display:block;font-size:14px;color:#54606C;white-space:nowrap;')}>{r.dom}</span>
                  </span>
                  <BuildTagged tag={r.src} keepSlot><span style={sx('display:inline-flex;align-items:center;height:16px;padding:0 5px;border-radius:4px;font-size:14px;font-weight:700;white-space:nowrap;' + (r.src === 'LIVE' ? 'color:#1F6B52;background:rgba(47,138,111,.12);' : 'color:#54606C;background:rgba(34,39,46,.07);'))}>{r.src}</span></BuildTagged>
                  <span style={sx('text-align:right;font-variant-numeric:tabular-nums;font-size:16px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;')}>{r.value}</span>
                  <span style={sx('text-align:right;font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;white-space:nowrap;')}>{r.pctl === null ? '–' : String(r.pctl)}</span>
                  <span
                    data-testid="perf-board-gap"
                    // "no gap · below floor" is wider than the 66px column: it wraps inside it
                    // rather than running under the Status column.
                    style={sx('text-align:right;font-variant-numeric:tabular-nums;font-weight:700;min-width:0;' + (gap === null ? 'font-size:14px;line-height:1.2;white-space:normal;' : 'font-size:16px;white-space:nowrap;') + 'color:' + (gap === null ? '#54606C' : gap >= 0 ? '#1F6B52' : '#B3261E') + ';')}
                  >
                    {gap === null ? 'no gap · below floor' : (gap > 0 ? '+' + gap : String(gap)) + ' pts'}
                  </span>
                  <span style={sx('display:flex;align-items:center;gap:6px;min-width:0;')}>
                    <span style={sx('width:7px;height:7px;border-radius:50%;flex:0 0 7px;background:' + stt[1] + ';')} />
                    <span style={sx('font-size:14px;font-weight:700;white-space:nowrap;color:' + stt[1] + ';')}>{stt[0]}</span>
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        <div style={sx(detailStyle)} data-testid="perf-detail">
          <div style={sx('flex:0 0 auto;display:flex;align-items:flex-start;gap:11px;padding:9px 12px 10px;border-bottom:1px solid rgba(34,39,46,.11);min-width:0;')}>
            {!wide && detailOpen && (
              <a
                href="#"
                onClick={(e) => {
                  pre(e);
                  setDetailOpen(false);
                }}
                style={sx('display:inline-flex;align-items:center;gap:5px;font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}
              >
                {'‹ Board'}
              </a>
            )}
            <span style={sx('flex:0 0 auto;display:flex;align-items:center;justify-content:center;width:44px;height:44px;border-radius:10px;font-size:22px;font-weight:800;letter-spacing:-.02em;color:' + gs[0] + ';background:' + gs[1] + ';')}>{grade ?? '–'}</span>
            <span style={sx('flex:1 1 auto;min-width:0;')}>
              <span style={sx('display:flex;align-items:baseline;gap:7px;flex-wrap:wrap;row-gap:2px;min-width:0;')}>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:19px;font-weight:800;letter-spacing:-.025em;line-height:1;')}>{comp ?? '–'}</span>
                <span style={sx('font-size:14px;color:#54606C;')}>of 100</span>
              </span>
              <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:3px;line-height:1.35;text-wrap:pretty;')}>
                <span>{bandNote}</span> · <span>{peerLabel}</span>
              </span>
              <span style={sx('display:block;height:5px;border-radius:3px;background:rgba(34,39,46,.10);margin-top:8px;position:relative;')}>
                <i style={sx('position:absolute;top:50%;left:' + bandPos + '%;transform:translate(-50%,-50%) rotate(45deg);width:9px;height:9px;border-radius:2px;background:' + (bandP === null ? '#54606C' : pc(bandP)) + ';box-shadow:0 0 0 2px #FCFAF4;')} />
                <i
                  title={fc ? 'Straight line from the last 90 days of domain scores. An estimate, not a promise.' : ''}
                  style={sx(fc ? 'position:absolute;top:50%;left:' + fcPos + '%;transform:translate(-50%,-50%) rotate(45deg);width:9px;height:9px;border-radius:2px;border:1.5px solid #2F5C97;box-sizing:border-box;background:transparent;animation:fcPulse 2.4s ease-in-out infinite;' : 'display:none;')}
                />
              </span>
              <span style={sx('display:flex;align-items:baseline;justify-content:space-between;gap:6px;font-size:14px;color:#54606C;margin-top:3px;')}>
                <span>p10</span>
                <span>p50 median</span>
                <span>p90</span>
              </span>
            </span>
          </div>
          <div style={sx('flex:0 0 auto;padding:8px 22px 9px 12px;border-bottom:1px solid rgba(34,39,46,.11);min-width:0;')}>
            <div style={sx('font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;margin-bottom:5px;')}>What moves the grade</div>
            {why.map((w, i) => {
              const drag = i === 2;
              const color = drag ? (w[1] < 40 ? '#B3261E' : '#5B6B7C') : '#1F6B52';
              return (
                <div key={w[0]} style={sx('animation:riseIn .3s cubic-bezier(.22,.61,.36,1);animation-delay:' + i * 60 + 'ms;display:flex;align-items:baseline;gap:7px;min-width:0;padding:2px 0;')}>
                  <span style={sx('font-size:14px;color:' + color + ';flex:0 0 auto;')}>{drag ? '▼' : '▲'}</span>
                  <span style={sx('flex:1 1 auto;min-width:0;font-size:14px;font-weight:600;line-height:1.35;text-wrap:pretty;')}>{drag ? w[0] + ' is the drag' : w[0] + (i === 0 ? ' carries the grade' : ' is well ahead')}</span>
                  <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;white-space:nowrap;flex:0 0 auto;')}>{ord(w[1])}</span>
                </div>
              );
            })}
          </div>
          <div style={sx('grid-column:1/-1;flex:0 0 auto;padding:7px 12px 8px;border-top:1px solid rgba(34,39,46,.11);min-width:0;')} data-testid="perf-selected">
            <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:nowrap;overflow:hidden;min-width:0;')}>
              <span style={sx('font-size:16px;font-weight:700;letter-spacing:-.012em;white-space:nowrap;')}>{sel?.label ?? ''}</span>
              <span style={sx('font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;white-space:nowrap;')}>{sel?.value ?? ''}</span>
              <span style={sx(selVerdictStyle)}>{selVerdict}</span>
            </div>
            <div title={selBasis} style={sx('font-size:14px;color:#3C454F;line-height:1.4;margin-top:4px;text-wrap:pretty;')}>
              {selGap}
            </div>
            {selHas && (
              <div style={sx('margin-top:7px;min-width:0;')}>
                <div style={sx('font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;')}>Drivers</div>
                <span style={sx('display:block;height:6px;border-radius:3px;background:rgba(34,39,46,.10);margin-top:7px;position:relative;')}>
                  <i style={sx('display:block;height:100%;border-radius:3px;width:' + sp + '%;background:' + pc(sp) + ';transform-origin:left;animation:growW .5s cubic-bezier(.22,.61,.36,1) forwards;')} />
                  <i style={sx('position:absolute;top:-3px;bottom:-3px;left:50%;width:2px;background:#22272E;')} />
                </span>
                <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:3px;margin-top:5px;min-width:0;')}>
                  {(
                    [
                      ['actual ' + ord(sp), pc(sp)],
                      ['target 50th', '#22272E'],
                      ['typical for ' + peerLabel, '#54606C'],
                    ] as const
                  ).map(([t, c]) => (
                    <span key={t} style={sx('display:inline-flex;align-items:center;gap:5px;font-size:14px;color:#54606C;white-space:nowrap;')}>
                      <span style={sx('width:7px;height:7px;border-radius:2px;flex:0 0 7px;background:' + c + ';')} />
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div style={sx('grid-column:1/-1;flex:0 0 auto;padding:6px 12px 0;min-width:0;')}>
            <div style={sx('font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;')}>Provenance</div>
            {[
              'n = ' + (residents ?? '–') + ' residents' + (o?.residentDays != null ? ' · ' + o.residentDays.toLocaleString('en-US') + ' resident-days' : ''),
              'peer set n = ' + (ctx.peer?.n ?? fac.peerN ?? 0).toLocaleString('en-US') + ' · ' + (ctx.peer?.name ?? 'national'),
              'Inspection metrics are ranked within state, the way CMS scores them: not against the ' + (ctx.peer?.name ?? 'national') + ' set used for the other three domains.',
              'CMS raised every Quality Measure rating threshold at the July 2026 refresh, so a QM star can fall with no change in care.',
            ].map((t, pi) => (
              <span key={pi} style={sx('display:block;font-size:14px;color:#54606C;line-height:1.4;padding:3px 0;text-wrap:pretty;' + (pi > 0 ? 'border-top:1px solid rgba(34,39,46,.06);' : ''))}>
                {t}
              </span>
            ))}
          </div>
          <div style={sx('grid-column:1/-1;flex:0 0 auto;min-width:0;padding:0 12px 11px;')} data-testid="perf-ladder">
            <div style={sx('display:flex;align-items:center;gap:7px;flex-wrap:nowrap;overflow:hidden;padding-top:5px;border-top:1px solid rgba(34,39,46,.11);min-width:0;')}>
              <span style={sx('font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;white-space:nowrap;')}>Margin impact</span>
              <span style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>whole facility</span>
              <span style={sx('flex:1 1 6px;')} />
              <input
                type="text"
                aria-label="Operating margin, your figure"
                value={typed ?? ''}
                onChange={(e) => setTyped(e.target.value)}
                placeholder="2.0"
                style={sx('box-sizing:border-box;width:82px;height:30px;padding:0 9px;border-radius:7px;border:1px solid rgba(34,39,46,.2);background:#FCFAF4;font-family:inherit;font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;color:#22272E;')}
              />
              <span style={sx('font-size:16px;font-weight:700;color:#54606C;')}>%</span>
              <a
                href="#"
                onClick={(e) => {
                  pre(e);
                  setTyped(null);
                }}
                style={sx('display:inline-flex;align-items:center;height:30px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;background:rgba(34,39,46,.05);color:#3C454F;border:1px solid rgba(34,39,46,.16);' + (mlBasis === 'WHAT-IF' ? '' : 'display:none;'))}
              >
                Revert
              </a>
            </div>
            <div style={sx('font-size:14px;color:#54606C;margin-top:2px;line-height:1.25;text-wrap:pretty;')}>{mlBasisNote}</div>
            <div style={sx('margin-top:2px;min-width:0;')}>
              {ML ? (
                ML.map((r, i) => {
                  const pct = mlBase + mlPts(r.usd);
                  const top = i === ML.length - 1;
                  const tone = i === 0 ? '#54606C' : top ? '#12503C' : '#1F6B52';
                  return (
                    <div key={r.k} data-testid="perf-rung" style={sx('display:grid;grid-template-columns:minmax(0,1fr) 58px 72px;gap:8px;align-items:baseline;padding:1px 0;border-top:1px solid rgba(34,39,46,.07);min-width:0;')}>
                      <span style={sx('display:flex;align-items:baseline;gap:6px;flex-wrap:nowrap;overflow:hidden;min-width:0;')}>
                        <span style={sx('font-size:14px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;')}>{r.k}</span>
                        <BuildTagged tag={r.basis}><span style={sx(basisTag(r.basis) + 'flex:0 0 auto;')}>{r.basis}</span></BuildTagged>
                      </span>
                      <span style={sx('font-variant-numeric:tabular-nums;font-size:' + (top ? '19px' : '16px') + ';font-weight:' + (top ? '800' : '700') + ';letter-spacing:-.02em;white-space:nowrap;color:' + tone + ';')}>{pct.toFixed(2) + '%'}</span>
                      <span style={sx('text-align:right;font-variant-numeric:tabular-nums;font-size:16px;font-weight:700;color:#3C454F;white-space:nowrap;')}>
                        {i === 0 ? '–' : '+$' + Math.round(r.usd - ML[i - 1].usd).toLocaleString('en-US') + '/mo'}
                      </span>
                    </div>
                  );
                })
              ) : (
                <span style={sx('display:block;font-size:14px;color:#54606C;padding:3px 0;')}>{plain('Not served yet: the revenue base and the capture dollars the ladder is built on.')}</span>
              )}
            </div>
            {ML && (
              <div style={sx('display:flex;align-items:flex-start;gap:12px;flex-wrap:wrap;row-gap:3px;margin-top:4px;padding-top:4px;border-top:1px solid rgba(34,39,46,.12);min-width:0;')}>
                <span style={sx('flex:1 1 210px;min-width:0;font-size:14px;font-weight:700;color:#3C454F;line-height:1.25;text-wrap:pretty;')}>
                  {'Cumulative at the top rung: +$' + Math.round(ML[ML.length - 1].usd).toLocaleString('en-US') + '/mo against today, worth ' + mlPts(ML[ML.length - 1].usd).toFixed(2) + ' points.'}
                </span>
                <span
                  title={'Two denominators, kept apart. Published rates are per ENROLLED resident. The pool above is per resident in the BUILDING, because not every resident carries both codes.'}
                  style={sx('flex:1 1 210px;min-width:0;font-size:14px;color:#54606C;line-height:1.25;text-wrap:pretty;')}
                >
                  {'Two denominators, kept apart · published rates are per ENROLLED resident, the pool is per resident in the BUILDING ($' + (mlPerRes ?? 0).toFixed(2) + '/mo across ' + (residents ?? '–') + ').'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Restored from production (not in the design): frailty / CHESS risk and medication adherence. */}
      <CareOpsRisk org={facility} enabled={enabled} />
    </div>
  );
}
